import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  MessageCircle,
  TrendingDown,
  Brain,
  Target,
  Users,
  BarChart3,
  AlertTriangle,
  FileText,
  Clock,
  Sparkles,
  CheckCircle2,
  Send,
  ChevronRight,
  Menu,
  X,
  Mail,
  Building2,
  User,
  GraduationCap,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_logo-presentation-2/artifacts/x9i3s1mk_ChatGPT%20Image%2024%20de%20fev.%20de%202026%2C%2010_10_12.png";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#problema", label: "Problema" },
    { href: "#solucao", label: "Solução" },
    { href: "#beneficios", label: "Benefícios" },
    { href: "#precos", label: "Preços" },
    { href: "#equipe", label: "Equipe" },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-4" : "py-6"
      }`}
      data-testid="navigation"
    >
      <div className="container-main flex items-center justify-between">
        <a href="#" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="Reforce" className="h-10 w-auto" />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="nav-link"
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
          <a href="#contato" className="btn-primary text-sm" data-testid="nav-cta">
            Falar com Especialista
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-4"
          >
            <div className="container-main py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="nav-link py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contato"
                className="btn-primary text-sm text-center mt-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Falar com Especialista
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      className="relative min-h-screen flex items-center pt-24"
      data-testid="hero-section"
    >
      <div className="hero-glow"></div>
      <div className="container-main relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
              Tutor de IA para EAD
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              Diga Adeus ao{" "}
              <span className="text-[#E5FF00]">Churn</span> no EAD
            </h1>
            <p className="text-lg md:text-xl text-[#A1A1AA] mb-8 max-w-xl">
              Uma IA conversacional que transforma o engajamento e garante a
              retenção dos seus alunos com suporte acadêmico e emocional 24/7.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contato" className="btn-primary" data-testid="hero-cta-primary">
                Falar com Especialista
              </a>
              <a href="#demo" className="btn-secondary" data-testid="hero-cta-secondary">
                Ver Demo
              </a>
            </div>

            <div className="flex items-center gap-8 mt-12 pt-8 border-t border-[#27272A]">
              <div>
                <div className="stat-number">30-50%</div>
                <p className="text-sm text-[#A1A1AA] mt-1">Taxa de abandono</p>
              </div>
              <div>
                <div className="stat-number">60</div>
                <p className="text-sm text-[#A1A1AA] mt-1">Dias críticos</p>
              </div>
              <div>
                <div className="stat-number">24/7</div>
                <p className="text-sm text-[#A1A1AA] mt-1">Suporte IA</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block"
          >
            <ChatDemo />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Chat Demo Component
const DEMO_CONVERSATION = [
  {
    type: "ai",
    text: "Olá! Sou o Reforcinho, seu tutor de IA. Como posso te ajudar hoje?",
  },
  {
    type: "user",
    text: "Estou com dificuldade em entender derivadas",
  },
  {
    type: "ai",
    text: "Entendo! Derivadas podem parecer complicadas no início. Vamos começar pelo básico: a derivada mede a taxa de variação de uma função. Quer que eu explique com um exemplo prático do seu dia a dia?",
  },
  {
    type: "user",
    text: "Sim, por favor!",
  },
  {
    type: "ai",
    text: "Imagine a velocidade do seu carro. A posição é a função, e a derivada dessa posição em relação ao tempo é a velocidade! Se você está a 60 km/h, isso é a taxa de variação da sua posição. Ficou mais claro? Posso criar um exercício personalizado pra você praticar!",
  },
];

const ChatDemo = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [demoComplete, setDemoComplete] = useState(false);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (demoComplete || currentIndex >= DEMO_CONVERSATION.length) {
      setDemoComplete(true);
      setIsTyping(false);
      return;
    }

    const currentMessage = DEMO_CONVERSATION[currentIndex];
    
    if (currentMessage.type === "ai") {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, currentMessage]);
        setIsTyping(false);
        setCurrentIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, currentMessage]);
        setCurrentIndex(prev => prev + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, demoComplete]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    setMessages((prev) => [...prev, { type: "user", text: inputValue }]);
    setInputValue("");
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          type: "ai",
          text: "Essa é uma ótima pergunta! Como tutor de IA, estou aqui para te ajudar a entender qualquer conceito. Continue praticando e não hesite em perguntar!",
        },
      ]);
      setIsTyping(false);
    }, 2000);
  };

  return (
    <div className="chat-container p-6" id="demo" data-testid="chat-demo">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#27272A]">
        <div className="w-10 h-10 rounded-full bg-[#E5FF00] flex items-center justify-center">
          <Brain size={20} className="text-black" />
        </div>
        <div>
          <h4 className="font-semibold">Reforcinho</h4>
          <p className="text-xs text-[#A1A1AA]">Tutor de IA Online</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          <span className="text-xs text-[#A1A1AA]">Online</span>
        </div>
      </div>

      <div ref={chatContainerRef} className="h-80 overflow-y-auto space-y-4 mb-4">
        <AnimatePresence>
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.type === "user"
                    ? "bg-[#E5FF00] text-black rounded-br-md"
                    : "bg-[#27272A] text-white rounded-bl-md"
                }`}
              >
                <p className="text-sm">{msg.text}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-[#27272A] p-3 rounded-2xl rounded-bl-md">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSend()}
          placeholder="Digite sua dúvida..."
          className="form-input flex-1"
          data-testid="chat-input"
        />
        <button
          onClick={handleSend}
          className="btn-primary px-4"
          data-testid="chat-send-btn"
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

// Problem Section
const ProblemSection = () => {
  return (
    <section
      id="problema"
      className="section-padding"
      data-testid="problem-section"
    >
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-red-500/10 text-red-400 mb-6">
              O Problema
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Alto <span className="text-red-400">Churn</span> no EAD
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-8">
              30-50% dos alunos abandonam cursos online nos primeiros 60 dias.
              Segundo a Deloitte, a principal causa é a falta de engajamento e
              pertencimento.
            </p>

            <div className="space-y-4">
              {[
                { icon: TrendingDown, text: "Alunos se sentem perdidos" },
                { icon: AlertTriangle, text: "Sobrecarregados pelo volume de conteúdo" },
                { icon: Clock, text: "Desmotivados pela ausência de feedback imediato" },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <item.icon size={20} className="text-red-400" />
                  </div>
                  <p className="text-[#EDEDED]">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="aspect-video rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758685848208-e108b6af94cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhZHVsdCUyMHVuaXZlcnNpdHklMjBzdHVkZW50JTIwbGFwdG9wJTIwb25saW5lJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzcxOTQwOTA0fDA&ixlib=rb-4.1.0&q=85"
                alt="Universitária estudando online"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 p-6 glass rounded-xl max-w-xs">
              <div className="stat-number text-red-400">-50%</div>
              <p className="text-sm text-[#A1A1AA]">
                dos alunos abandonam em 60 dias
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  const features = [
    {
      icon: MessageCircle,
      title: "Engajamento Proativo",
      description:
        "A IA inicia conversas, verifica o progresso e envia lembretes motivacionais.",
    },
    {
      icon: Brain,
      title: "Reforço Personalizado",
      description:
        "Resume aulas, identifica lacunas de conhecimento e cria exercícios focados.",
    },
    {
      icon: Target,
      title: "Foco no Essencial",
      description:
        "Analisa provas passadas para direcionar o estudo para tópicos de maior probabilidade.",
    },
  ];

  return (
    <section
      id="solucao"
      className="section-padding relative"
      data-testid="solution-section"
    >
      <div className="absolute inset-0 gradient-glow opacity-50"></div>
      <div className="container-main relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
            Nossa Solução
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Tutor de <span className="text-[#E5FF00]">IA</span> que Garante a
            Retenção
          </h2>
          <p className="text-lg text-[#A1A1AA]">
            Uma IA conversacional acessível via chat (web, app, WhatsApp) que
            oferece suporte acadêmico e emocional 24/7.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bento-card text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#E5FF00]/10 flex items-center justify-center mx-auto mb-6">
                <feature.icon size={28} className="text-[#E5FF00]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-[#A1A1AA]">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="aspect-video rounded-2xl overflow-hidden neon-glow">
            <img
              src="https://images.pexels.com/photos/8199653/pexels-photo-8199653.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Universitária na biblioteca com laptop"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const studentBenefits = [
    { icon: Clock, text: "Disponível 24/7" },
    { icon: MessageCircle, text: "Tirar dúvidas complexas" },
    { icon: FileText, text: "Resumir capítulos longos" },
    { icon: Target, text: "Simulados com foco em pontos fracos" },
    { icon: Sparkles, text: "Palavras de incentivo e motivação" },
  ];

  const institutionBenefits = [
    { icon: BarChart3, text: "Dashboard B2B em tempo real" },
    { icon: Users, text: "Métricas de engajamento" },
    { icon: AlertTriangle, text: "Alertas de risco de churn" },
    { icon: FileText, text: "Relatórios de eficácia do conteúdo" },
    { icon: Target, text: "Identificação de tópicos problemáticos" },
  ];

  return (
    <section
      id="beneficios"
      className="section-padding"
      data-testid="benefits-section"
    >
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
            Benefícios
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Para <span className="text-[#E5FF00]">Todos</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Student Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bento-card"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <GraduationCap size={24} className="text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold">Para o Aluno</h3>
            </div>
            <div className="space-y-4">
              {studentBenefits.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#27272A] flex items-center justify-center">
                    <item.icon size={16} className="text-[#E5FF00]" />
                  </div>
                  <p className="text-[#EDEDED]">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Institution Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bento-card"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Building2 size={24} className="text-purple-400" />
              </div>
              <h3 className="text-2xl font-semibold">Para a Instituição</h3>
            </div>
            <div className="space-y-4">
              {institutionBenefits.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-[#27272A] flex items-center justify-center">
                    <item.icon size={16} className="text-[#E5FF00]" />
                  </div>
                  <p className="text-[#EDEDED]">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const plans = [
    {
      name: "Tier 1",
      students: "Até 5.000 alunos",
      price: "5,00",
      description: "Ideal para instituições menores",
      features: [
        "Tutor IA 24/7",
        "Dashboard básico",
        "Suporte por email",
        "Integração web",
      ],
      featured: false,
    },
    {
      name: "Tier 2",
      students: "5.001 a 20.000 alunos",
      price: "4,00",
      description: "Para instituições em crescimento",
      features: [
        "Tudo do Tier 1",
        "Dashboard avançado",
        "Alertas de churn",
        "Integração WhatsApp",
        "Suporte prioritário",
      ],
      featured: true,
    },
    {
      name: "Tier 3",
      students: "Acima de 20.000 alunos",
      price: "3,00",
      description: "Para grandes instituições",
      features: [
        "Tudo do Tier 2",
        "API dedicada",
        "Customização total",
        "Gerente de conta",
        "SLA garantido",
      ],
      featured: false,
    },
  ];

  return (
    <section
      id="precos"
      className="section-padding"
      data-testid="pricing-section"
    >
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
            Preços
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Preços <span className="text-[#E5FF00]">Escaláveis</span>
          </h2>
          <p className="text-lg text-[#A1A1AA]">
            Modelo SaaS B2B com preço por aluno ativo (PPA). Quanto mais alunos,
            menor o custo.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`pricing-card p-8 ${plan.featured ? "featured" : ""}`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.featured && (
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#E5FF00] text-black mb-4">
                  Mais Popular
                </span>
              )}
              <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
              <p className="text-sm text-[#A1A1AA] mb-4">{plan.students}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-[#E5FF00]">
                  R$ {plan.price}
                </span>
                <span className="text-[#A1A1AA]">/aluno/mês</span>
              </div>
              <p className="text-sm text-[#A1A1AA] mb-6">{plan.description}</p>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 size={16} className="text-[#E5FF00]" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contato"
                className={`block text-center w-full ${
                  plan.featured ? "btn-primary" : "btn-secondary"
                }`}
              >
                Começar Agora
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const team = [
    {
      name: "Fred Santoro",
      role: "CEO & Visão",
      bio: "Ex-Diretor da AWS, Co-Founder do Zig, sócio da NEO Educação.",
    },
    {
      name: "Fernando Fernandes",
      role: "CTO & Tecnologia",
      bio: "Ex-OI e GPA, Founder do içougue.",
    },
  ];

  return (
    <section
      id="equipe"
      className="section-padding"
      data-testid="team-section"
    >
      <div className="container-main">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
            Equipe
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Quem está por <span className="text-[#E5FF00]">trás</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {team.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bento-card flex flex-col items-center text-center"
            >
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#E5FF00]/20 to-[#E5FF00]/5 flex items-center justify-center mb-6">
                <User size={40} className="text-[#E5FF00]" />
              </div>
              <h3 className="text-xl font-bold mb-1">{member.name}</h3>
              <p className="text-[#E5FF00] text-sm font-medium mb-4">
                {member.role}
              </p>
              <p className="text-[#A1A1AA]">{member.bio}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    students_count: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setFormData({
        name: "",
        email: "",
        company: "",
        students_count: "",
        message: "",
      });
    } catch (error) {
      toast.error("Erro ao enviar mensagem. Tente novamente.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contato"
      className="section-padding"
      data-testid="contact-section"
    >
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#E5FF00]/10 text-[#E5FF00] mb-6">
              Contato
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Vamos <span className="text-[#E5FF00]">Conversar?</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-8">
              Entre em contato para uma demonstração personalizada e descubra
              como o REFORCE pode transformar a retenção dos seus alunos.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#E5FF00]/10 flex items-center justify-center">
                  <Mail size={24} className="text-[#E5FF00]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Email</p>
                  <a
                    href="mailto:fernando@reforce.ai"
                    className="text-[#EDEDED] hover:text-[#E5FF00] transition-colors"
                  >
                    fernando@reforce.ai
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  required
                  className="form-input"
                  data-testid="contact-name"
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Seu email"
                  required
                  className="form-input"
                  data-testid="contact-email"
                />
              </div>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Nome da instituição"
                className="form-input"
                data-testid="contact-company"
              />
              <select
                name="students_count"
                value={formData.students_count}
                onChange={handleChange}
                className="form-input"
                data-testid="contact-students"
              >
                <option value="">Quantidade de alunos</option>
                <option value="menos_5000">Menos de 5.000</option>
                <option value="5000_20000">5.000 a 20.000</option>
                <option value="mais_20000">Mais de 20.000</option>
              </select>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Sua mensagem"
                rows={4}
                className="form-input resize-none"
                data-testid="contact-message"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  "Enviando..."
                ) : (
                  <>
                    Enviar Mensagem <ChevronRight size={18} />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-[#27272A]" data-testid="footer">
      <div className="container-main">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Reforce" className="h-8 w-auto" />
          </div>
          <p className="text-sm text-[#A1A1AA]">
            © {new Date().getFullYear()} REFORCE. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      <Toaster position="top-right" richColors />
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <BenefitsSection />
      <PricingSection />
      <TeamSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
